/**
 * Access-level enum — namespaced i18n keys stored in `auth_user.access_level`,
 * FK'd to `semantic_key`. The string IS the i18n lookup key, so
 * `$t(user.access_level)` yields the translated label. Extend by inserting
 * the new key into `semantic_key` in the migration that adds it, and adding
 * the corresponding member to the backend `AccessLevel` IntEnum.
 */
export type AccessLevel =
  | 'access_level.admin'
  | 'access_level.manager'
  | 'access_level.user'
  | 'access_level.partner'

/** Authenticated user info — flat shape used by navigation and sidebar */
export interface AuthUser {
  id: number
  email: string
  username: string | null
  first_name: string
  last_name: string
  access_level: AccessLevel
  /** Grants the PMS manager dashboard; independent of `access_level`. */
  is_leader: boolean
  /** Signed avatar URL — hydrated from /auth-user/me/avatar-url after fetch */
  avatar_url: string | null
  /** Whether the backend has an avatar_id set (independent of signed-URL hydration) */
  has_avatar: boolean
  picture_url?: string
  job_title?: string
  /** Bound partner — present only for users with access_level.partner */
  fiscozen_partner_id?: number | null
  /** Set when an admin is impersonating this user via /hijack */
  impersonated_by?: number | null
}

/** Personal detail as returned by the backend */
export interface PersonalDetailInfo {
  id: number
  auth_user_id: number
  first_name: string
  last_name: string
  picture_url: string | null
  private_phone: string | null
  private_email: string | null
  address: string | null
  linkedin_url: string | null
  gender: string | null
  birth_date: string | null
  birth_place: string | null
  birth_country: string | null
  citizenship: string | null
  employee_number: string | null
  social_security_number: string | null
  vat_number: string | null
  phone_visible: boolean
  created_at: string
  updated_at: string
}

/** Full user response from GET /auth-user/me or GET /auth-user/:id */
export interface CurrentUserResponse {
  id: number
  email: string
  username: string | null
  google_id: string | null
  is_active: boolean
  avatar_id: number | null
  created_at: string
  updated_at: string
  current_detail?: PersonalDetailInfo | null
  role_name?: string | null
  access_level: string | null
  is_leader: boolean
  /** Bound partner id — only set for partner-level users */
  fiscozen_partner_id?: number | null
  /** Set when an admin is impersonating this user via /hijack */
  impersonated_by?: number | null
}

/** Signed-URL payload returned by GET /auth-user/{id}/avatar-url */
export interface AvatarSignedUrl {
  id: number
  download_url: string
  download_url_expires_at: string
}

/**
 * Access-level display labels — values are i18n keys resolved via $t().
 * Since the stored value IS already the i18n key, this map is an identity,
 * kept for readability at call sites that want a labeled lookup.
 */
export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  'access_level.admin': 'access_level.admin',
  'access_level.manager': 'access_level.manager',
  'access_level.user': 'access_level.user',
  'access_level.partner': 'access_level.partner',
}

/** Access-level hierarchy for permission checks — higher index = more privilege */
export const ACCESS_LEVEL_HIERARCHY: AccessLevel[] = [
  'access_level.user',
  'access_level.manager',
  'access_level.admin',
]

/** Check if an access level meets the given minimum. */
export function hasMinAccessLevel(
  userAccessLevel: AccessLevel,
  minAccessLevel: AccessLevel,
): boolean {
  return (
    ACCESS_LEVEL_HIERARCHY.indexOf(userAccessLevel) >=
    ACCESS_LEVEL_HIERARCHY.indexOf(minAccessLevel)
  )
}

/** Admins and leaders see the PMS manager dashboard (`GET /pms/dashboard` 403s everyone else). */
export function canViewDashboard(
  user: Pick<AuthUser, 'access_level' | 'is_leader'> | null,
): boolean {
  if (!user) return false
  return user.access_level === 'access_level.admin' || user.is_leader
}
